import {DataStatus} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useId, useState} from 'react';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {MessageContextHostRecord} from '../types/MessageContextHostRecord';
import {MessageDayGroup} from '../types/MessageDayGroup';
import {ThreadReadState} from '../types/ThreadReadState';
import {findParentMessage} from '../utils/findParentMessage';
import {getMessageContextLinks} from '../utils/getMessageContextLinks';
import {RunOnMount} from './RunOnMount';
import {ThreadCallCard, ThreadCallPairing} from './ThreadCallCard';
import {ThreadDateSeparator} from './ThreadDateSeparator';
import {ThreadEmailCard} from './ThreadEmailCard';
import {ThreadMessageBubble} from './ThreadMessageBubble';

interface CommunicationThreadFeedProps {
  groups: MessageDayGroup[];
  isBasicMessagingOnly?: boolean;
  isLoading: boolean;
  isError: boolean;
  /**
   * Record the thread is rendered on. When set, each message gets buttons opening the records it
   * is linked to; without it messages render no links at all.
   */
  contextLinkHostRecord?: MessageContextHostRecord;
  /** Enables the "new" markers and the manual read toggles. */
  readState?: ThreadReadState;
  contextLinkLabels?: Record<string, string | undefined>;
  includeHostContextLink?: boolean;
  preferPrimaryContextLink?: boolean;
  showOutgoingRecipient?: boolean;
  /** Message to scroll into view once it is rendered, e.g. the oldest unread one. */
  scrollToMessageId?: string;
  /** Called once the feed scrolled to `scrollToMessageId`, e.g. to clear a one-time request. */
  onScrolledToMessage?: () => void;
  /** Offers pairing call rows with the record the thread is on; without it calls only show. */
  callPairing?: ThreadCallPairing;
  /** Lets the viewer write call notes; without it a call's note only shows. */
  canEditCallNotes?: boolean;
  /** Offers "Detail" on call rows, opening the call's own page. */
  canOpenCall?: boolean;
  /** Shows the recording state on call rows and lets the viewer play it. */
  canListenRecording?: boolean;
}

const getMessageAnchorId = (feedId: string, messageId: string) => `${feedId}-message-${messageId}`;

export function CommunicationThreadFeed(props: CommunicationThreadFeedProps) {
  // Each chip click replaces the highlight; otherwise it persists until the feed unmounts.
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const feedId = useId();
  const scrollToMessage = (messageId: string | undefined) => {
    if (isNil(messageId)) {
      return;
    }
    document.getElementById(getMessageAnchorId(feedId, messageId))?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };
  const handleParentChipClick = (parentMessageId: string) => {
    setHighlightedMessageId(parentMessageId);
    scrollToMessage(parentMessageId);
  };
  const hostRecord = props.contextLinkHostRecord;

  return (
    <DataStatus
      isLoading={props.isLoading}
      isError={props.isError}
      isEmpty={isEmpty(props.groups)}
      minHeight={60}
    >
      <VStack spacing={4}>
        {props.groups.map((group) => (
          <VStack key={group.dayKey} spacing={4}>
            <ThreadDateSeparator date={group.date} />
            {group.messages.map((message) => {
              const resolvedContextLinks = isNil(hostRecord)
                ? []
                : getMessageContextLinks(message, hostRecord, props.includeHostContextLink);
              const primaryContextLink = resolvedContextLinks.find(
                (link) =>
                  link.recordId === message.recordId && link.resourceId === message.resourceId
              );
              const contextLinks = (
                props.preferPrimaryContextLink && primaryContextLink
                  ? [primaryContextLink]
                  : resolvedContextLinks
              ).map((link) => ({...link, label: props.contextLinkLabels?.[link.recordId]}));

              return match(message.channel)
                .with('email', () => (
                  <ThreadEmailCard
                    key={message.id}
                    isBasicMessagingOnly={props.isBasicMessagingOnly}
                    message={message}
                    contextLinks={contextLinks}
                    parentMessage={findParentMessage(props.groups, message)}
                    isHighlighted={message.id === highlightedMessageId}
                    anchorId={getMessageAnchorId(feedId, message.id)}
                    onParentChipClick={handleParentChipClick}
                    showOutgoingRecipient={props.showOutgoingRecipient}
                    readState={props.readState}
                  />
                ))
                .with('sms', 'whatsapp', () => (
                  <Box key={message.id} id={getMessageAnchorId(feedId, message.id)}>
                    <ThreadMessageBubble
                      message={message}
                      contextLinks={contextLinks}
                      showOutgoingRecipient={props.showOutgoingRecipient}
                      readState={props.readState}
                    />
                  </Box>
                ))
                .with('call', () => (
                  <Show key={message.id} when={message.call}>
                    {(call) => (
                      <Box id={getMessageAnchorId(feedId, message.id)}>
                        <ThreadCallCard
                          message={message}
                          call={call}
                          contextLinks={contextLinks}
                          pairing={props.callPairing}
                          canEditNote={props.canEditCallNotes}
                          canOpenCall={props.canOpenCall}
                          canListenRecording={props.canListenRecording}
                          data-testid={`communicationThread-call-[${call.id}]`}
                        />
                      </Box>
                    )}
                  </Show>
                ))
                .exhaustive();
            })}
          </VStack>
        ))}
      </VStack>
      <Show when={isNotNil(props.scrollToMessageId)}>
        {/* Runs after the messages above mounted, so the anchor exists; a new target scrolls again. */}
        <RunOnMount
          key={props.scrollToMessageId}
          onMount={() => {
            scrollToMessage(props.scrollToMessageId);
            props.onScrolledToMessage?.();
          }}
        />
      </Show>
    </DataStatus>
  );
}
